import type { CarSpec, RulesCar, TeamSpec } from '../shared/defs';
import { compileCar } from '../shared/personas';
import { Api, $, h, toast } from './api';
import { Mic } from './mic';
import { ruleCard, ruleEditor } from './cardui';
import { carTag } from './carcolors';
import { talkIdeas } from './ideas';
import { Player, matchSummary, type ReplayData } from './replay';
import { teamLines } from './teamtext';
import { ruleText } from '../shared/rules';
import { getMap } from '../shared/map';
import { usedRulesOnly } from '../shared/classtrain';

const api = new Api();
let meta: any;
let st: any; // kid view from server
let talkCar = 0;
let editing: number | null = null;
// the open editor survives re-renders (a classmate joining or playing refreshes this page) so unsaved edits stay;
// it is rebuilt only when this car's saved rules change underneath it
let editor: { car: number; saved: string; el: HTMLElement } | null = null;
let highlight: { car: number; idx: number[] } | null = null;
const player = new Player({ mySide: 0 });
const roundPlayer = new Player({ mySide: 0 });
let shownMap: string | null = null; // the room's map, to tell the kid when the teacher switches it
$('#player').append(player.el);
$('#roundPlayer').append(roundPlayer.el);

const TOKEN_KEY = 'arena.kidToken'; // the room this device is in now
// every room this device has joined, so typing an old room code again brings back that same kid and team
const ROOMS_KEY = 'arena.kidRooms';
const rooms = (): Record<string, string> => { try { return JSON.parse(localStorage.getItem(ROOMS_KEY) || '{}'); } catch { return {}; } };
const keepRoom = (code: string, token: string | null) => {
  const r = rooms();
  if (token) r[code] = token; else delete r[code];
  try { localStorage.setItem(ROOMS_KEY, JSON.stringify(r)); } catch { /* private mode */ }
};
const busy = async <T>(btn: HTMLButtonElement | null, label: string, f: () => Promise<T>): Promise<T | undefined> => {
  const old = btn?.textContent;
  if (btn) { btn.disabled = true; btn.textContent = label; }
  try { return await f(); } catch (e: any) { toast(e.message, 'error'); } finally { if (btn) { btn.disabled = false; btn.textContent = old!; } }
};

// ---------- boot ----------
api.onStatus = (ok) => { $('#conn').textContent = ok ? '' : '⚠ 连接断了，正在重连…'; };
// only re-attach when already in a room: on the join form a reconnect must not pull the kid back into the old room
api.onReconnect = () => { const t = localStorage.getItem(TOKEN_KEY); if (t && st) resume(t, st.room); };
api.onPush = (m) => {
  if (!st) return; // on the join form: pushes from a room this device left are not for us
  if (m.t === 'removed') { keepRoom(String(st.room), null); localStorage.removeItem(TOKEN_KEY); showJoin(); toast('老师把你移出了这个房间，请重新输入房间号'); return; }
  if (m.t === 'round') { toast('老师开始了正式比赛，结果出来了！'); refresh(); }
  if (m.t === 'roster' || m.t === 'beats') refresh(true);
  if (m.t === 'asr' && streaming) say.value = streaming.prefix + m.text;
};

(async () => {
  meta = await api.call('meta');
  $('#provider').textContent = meta.provider === 'mock' ? '翻译：离线简易版' : '';
  setupVoice();
  const params = new URLSearchParams(location.search);
  if (params.get('room')) ($('#code') as HTMLInputElement).value = params.get('room')!;
  // a ?room= link for another room means the kid wants that room, not the one this browser is in now
  const want = params.get('room');
  const t = (want && rooms()[want]) || localStorage.getItem(TOKEN_KEY);
  if (t && !params.get('new')) await resume(t, want); else showJoin();
})();

function showJoin() { st = undefined; shownMap = null; $('#who').innerHTML = ''; $('#join').classList.remove('hidden'); $('#app').classList.add('hidden'); $('#tabs').classList.add('hidden'); }

async function resume(token: string, room?: string | number | null) {
  try {
    const v = await api.call('kid.resume', { token });
    if (room && String(v.room) !== String(room)) return showJoin();
    localStorage.setItem(TOKEN_KEY, token);
    keepRoom(String(v.room), token);
    st = v;
    start();
  } catch { if (localStorage.getItem(TOKEN_KEY) === token) localStorage.removeItem(TOKEN_KEY); showJoin(); }
}

// leave this room on this device; the join form then takes a new room code.
// The token stays saved, so typing the old room code again brings back the same team.
function leaveRoom() {
  ($('#code') as HTMLInputElement).value = '';
  history.replaceState(null, '', location.pathname);
  showJoin();
}

$('#joinBtn').onclick = () => busy($('#joinBtn'), '进入中…', async () => {
  const code = ($('#code') as HTMLInputElement).value.trim();
  const name = ($('#name') as HTMLInputElement).value.trim();
  const t = rooms()[code] ?? localStorage.getItem(TOKEN_KEY);
  if (t) { // back to a room this device was in: same kid, same team (a new join would clash with the old nickname)
    const v = await api.call('kid.resume', { token: t }).catch(() => null);
    if (v && String(v.room) === code) { localStorage.setItem(TOKEN_KEY, t); keepRoom(code, t); st = v; return start(); }
    if (!v) keepRoom(code, null);
  }
  const r = await api.call('kid.join', { code, name });
  localStorage.setItem(TOKEN_KEY, r.token);
  keepRoom(String(r.state.room), r.token);
  st = r.state;
  start();
});

function start() {
  $('#join').classList.add('hidden');
  $('#app').classList.remove('hidden');
  $('#tabs').classList.remove('hidden');
  renderAll();
}

async function refresh(quiet = false) {
  try { st = await api.call('kid.state'); renderAll(); } catch (e: any) { if (!quiet) toast(e.message, 'error'); }
}

document.querySelectorAll<HTMLButtonElement>('#tabs button').forEach((b) => {
  b.onclick = () => {
    document.querySelectorAll('#tabs button').forEach((x) => x.classList.toggle('active', x === b));
    document.querySelectorAll('.tab').forEach((t) => t.classList.toggle('hidden', t.id !== `tab-${b.dataset.tab}`));
  };
});

function renderAll() {
  const M = getMap(st.map);
  if (shownMap && shownMap !== M.id) toast(`老师把地图换成了「${M.name}」：${M.blurb}`);
  shownMap = M.id;
  player.mapId = roundPlayer.mapId = M.id;
  document.querySelector('a.navlink[href^="/rules"]')?.setAttribute('href', `/rules?map=${M.id}`);
  $('#who').innerHTML = '';
  $('#who').append(h('b', {}, st.name), h('span', { class: 'muted' }, ` · 房间 ${st.room} · 地图「${M.name}」 · 第 ${st.latest} 版`),
    st.locked != null ? h('span', { class: 'pill ok' }, `已锁定第 ${st.locked} 版`) : h('span', { class: 'pill warn' }, '还没锁定'),
    h('button', { class: 'btn tiny', onclick: () => { if (confirm('换一个房间号？以后再输入这个房间号，还能回到现在这支队伍。')) leaveRoom(); } }, '换房间'));
  renderCars();
  renderTalkTarget();
  renderVersions();
  renderOpponents();
  renderChampion();
  renderRounds();
  if (st.pending) showAnswer({ kind: 'clarify', question: st.pending.question, cannot: [], diff: '' }, true);
}

// ---------- cars ----------
function personaOf(id: string) { return meta.personas.find((p: any) => p.id === id); }

// A classmate joining or playing refreshes this page. Redrawing a part that has not changed would close a
// dropdown the kid has open (and lose what it shows), so each part with dropdowns is redrawn only when its data changes.
const drawn = new Map<HTMLElement, string>();
const unchanged = (el: HTMLElement, data: unknown) => {
  const key = JSON.stringify(data);
  if (drawn.get(el) === key) return true;
  drawn.set(el, key);
  return false;
};

function renderCars() {
  const box = $('#cars');
  const cars: CarSpec[] = st.team.cars;
  if (unchanged(box, { cars, editing, highlight })) return;
  box.innerHTML = '';
  if (editing == null || cars[editing]?.kind !== 'rules') editor = null;
  cars.forEach((car, i) => {
    const head = h('div', { class: 'carhead' }, carTag(i));
    const body = h('div', { class: 'carbody' });
    if (car.kind === 'rules') {
      head.append(h('span', { class: 'tag me' }, '我写的'),
        h('button', { class: 'btn tiny', onclick: () => { editing = editing === i ? null : i; renderCars(); } }, editing === i ? '收起' : '手动改'),
        h('button', { class: 'btn tiny', onclick: () => setCar(i, { kind: 'persona', persona: 'keeper', knobs: {} }) }, '交给机器人'));
      if (editing === i) {
        const saved = JSON.stringify(car);
        if (editor?.car !== i || editor.saved !== saved)
          editor = { car: i, saved, el: ruleEditor(car as RulesCar, (c) => { editing = null; setCar(i, c); }, () => { editing = null; renderCars(); }) };
        body.append(editor.el);
      } else body.append(ruleCard(car.rules, car.params, { highlight: highlight?.car === i ? highlight.idx : [] }));
    } else {
      const p = personaOf(car.persona);
      head.append(h('span', { class: 'tag bot' }, '机器人'),
        h('button', { class: 'btn tiny', onclick: () => { talkCar = i; setCar(i, { kind: 'rules', rules: [], params: undefined }); } }, '我来写这辆车'));
      const psel = h('select', { onchange: (e: any) => setCar(i, { kind: 'persona', persona: e.target.value, knobs: {} }) },
        ...meta.personas.map((x: any) => h('option', { value: x.id, selected: x.id === car.persona }, x.name)));
      const knobs = h('div', { class: 'knobs' }, ...p.knobs.map((k: any) => h('label', {}, k.label,
        h('select', { onchange: (e: any) => setCar(i, { ...car, knobs: { ...car.knobs, [k.id]: e.target.value } }) },
          ...Object.entries(k.options).map(([v, l]) => h('option', { value: v, selected: (car.knobs[k.id] ?? k.default) === v }, l as string))))));
      const details = h('details', {}, h('summary', {}, '看看它的规则'), ruleCard(compileCar(car).rules, undefined));
      body.append(h('div', { class: 'row' }, psel, h('span', { class: 'muted small' }, p.blurb)), knobs, details);
    }
    box.append(h('div', { class: `car ${car.kind}` }, head, body));
  });
}

async function setCar(i: number, spec: any) {
  try {
    st.team = await api.call('kid.setCar', { car: i, spec });
    await refresh(true);
  } catch (e: any) { toast(e.message, 'error'); }
}

function renderTalkTarget() {
  const box = $('#talkTarget');
  box.innerHTML = '';
  const rulesCars = st.team.cars.map((c: CarSpec, i: number) => [c, i]).filter(([c]: any) => c.kind === 'rules');
  if (!rulesCars.some(([, i]: any) => i === talkCar)) talkCar = rulesCars[0]?.[1] ?? 0;
  if (!rulesCars.length) { box.append(h('span', { class: 'muted' }, '三辆都是机器人。点某辆车上的“我来写这辆车”。')); $('#ideas').innerHTML = ''; return; }
  box.append(h('span', { class: 'muted' }, '对'), ...rulesCars.map(([, i]: any) =>
    h('button', { class: `chip ${i === talkCar ? 'on' : ''}`, onclick: () => { talkCar = i; renderTalkTarget(); } }, carTag(i))), h('span', { class: 'muted' }, '说：'));
  renderIdeas();
}

function renderIdeas() {
  const box = $('#ideas');
  box.innerHTML = '';
  const car = st.team.cars[talkCar];
  if (car?.kind !== 'rules') return;
  box.append(talkIdeas(talkCar, car.rules, (say) => {
    const ta = $('#say') as HTMLTextAreaElement;
    ta.value = say;
    ta.focus();
  }));
}

// ---------- talking ----------
$('#sendBtn').onclick = () => send();
($('#say') as HTMLTextAreaElement).onkeydown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

async function send() {
  const ta = $('#say') as HTMLTextAreaElement;
  const text = ta.value.trim();
  if (!text) return;
  await busy($('#sendBtn'), '翻译中…', async () => {
    const before = st.team.cars[talkCar]?.rules ?? [];
    const r = await api.call('kid.say', { car: talkCar, text });
    ta.value = '';
    if (r.kind === 'edit') {
      const keys = before.map((x: any) => JSON.stringify(x));
      highlight = { car: talkCar, idx: r.team.cars[talkCar].rules.map((x: any, i: number) => (keys.includes(JSON.stringify(x)) ? -1 : i)).filter((i: number) => i >= 0) };
    }
    showAnswer({ ...r, said: text });
    await refresh(true);
  });
}

function showAnswer(r: any, pendingOnly = false) {
  const box = $('#answer');
  box.innerHTML = '';
  if (r.said) box.append(h('div', { class: 'said' }, `你说：“${r.said}”`));
  if (r.kind === 'edit') box.append(h('div', { class: 'bubble ok' }, `我改好了：${r.diff}。看看规则卡，是不是你想的意思？`));
  if (r.kind === 'clarify') box.append(h('div', { class: 'bubble ask' }, `🤔 ${r.question}`, pendingOnly ? '' : h('div', { class: 'muted small' }, '直接回答就行，规则没有改。')));
  for (const c of r.cannot ?? []) box.append(h('div', { class: 'bubble no' }, c));
  if (r.kind === 'cannot' && !(r.cannot ?? []).length) box.append(h('div', { class: 'bubble no' }, '这个我做不到。'));
  if (r.provider && r.provider !== 'mock') box.append(h('div', { class: 'muted small' }, `${(r.ms / 1000).toFixed(1)} 秒`));
}

// voice: hold to talk, recognized text goes into the box for the kid to check before sending.
// Preferred: stream the microphone to our server, which relays to the speech service (works in mainland China,
// same on every device). Fallback: the browser's own recognizer (Safari sends it to Apple, Chrome to Google).
const mic = $('#mic') as HTMLButtonElement;
const say = $('#say') as HTMLTextAreaElement;
const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
const micIdle = () => { mic.classList.remove('on'); mic.disabled = false; mic.textContent = '🎤 按住说话'; };
const micOn = () => { mic.classList.add('on'); mic.textContent = '🔴 正在听… 松开结束'; };
let streaming: { prefix: string } | null = null; // while a server-side recognition is open
mic.oncontextmenu = (e) => e.preventDefault(); // long press on iPad would otherwise pop up a menu

function setupVoice() {
  if (meta?.asr && Mic.supported()) {
    const rec = new Mic();
    rec.onChunk = (pcm) => api.sendBinary(pcm);
    let held = false;
    mic.onpointerdown = async (e) => {
      e.preventDefault();
      if (held || streaming) return;
      held = true;
      streaming = { prefix: say.value };
      micOn();
      try {
        // both start inside the press: iOS only lets audio begin from a user gesture
        await Promise.all([api.call('kid.asrStart'), rec.start()]);
        if (!held) rec.stop(); // let go while the microphone was still starting
      } catch (err: any) {
        held = false; streaming = null; rec.stop(); micIdle();
        api.call('kid.asrStop').catch(() => {});
        toast(err?.name === 'NotAllowedError' ? '需要允许使用麦克风，才能说话输入' : '语音输入出错：' + (err?.message ?? err) + '，可以打字', 'error');
      }
    };
    const release = async () => {
      if (!held) return;
      held = false;
      rec.stop();
      mic.disabled = true; mic.textContent = '⏳ 正在识别…';
      const s = streaming;
      try {
        const r = await api.call('kid.asrStop');
        if (s && r?.text) say.value = s.prefix + r.text;
      } catch (err: any) { toast('语音识别出错：' + (err?.message ?? err) + '，可以打字', 'error'); }
      finally { if (streaming === s) streaming = null; micIdle(); }
    };
    mic.onpointerup = release; mic.onpointercancel = release; mic.onpointerleave = release;
    return;
  }
  if (!SR) { mic.title = '这个浏览器不支持语音输入，请打字'; mic.disabled = true; mic.textContent = '🎤 不支持语音'; return; }
  let rec: any = null;
  const stop = () => { rec?.stop(); micIdle(); };
  mic.onpointerdown = (e) => {
    e.preventDefault();
    rec = new SR(); rec.lang = 'zh-CN'; rec.interimResults = true; rec.continuous = true;
    const prefix = say.value;
    rec.onresult = (ev: any) => { let t = ''; for (const r of ev.results) t += r[0].transcript; say.value = prefix + t; };
    rec.onerror = (ev: any) => toast('语音识别出错：' + ev.error + '，可以打字', 'error');
    rec.start(); micOn();
  };
  mic.onpointerup = stop; mic.onpointerleave = () => { if (mic.classList.contains('on')) stop(); };
}

// ---------- versions ----------
function renderVersions() {
  const box = $('#versions');
  if (unchanged(box, [st.versions, st.locked, st.latest])) return;
  box.innerHTML = '';
  const sel = h('select', {}, ...[...st.versions].reverse().map((v: any) => h('option', { value: v.n }, `第${v.n}版 · ${v.note}${v.n === st.locked ? ' 🔒' : ''}`))) as HTMLSelectElement;
  box.append(
    h('div', { class: 'row wrap' }, sel,
      h('button', { class: 'btn small', onclick: () => busy(null, '', async () => { await api.call('kid.revert', { n: +sel.value }); highlight = null; await refresh(); toast(`回到了第${sel.value}版的规则`); }) }, '回到这一版'),
      h('button', { class: 'btn small primary', onclick: () => busy(null, '', async () => { await api.call('kid.lock'); await refresh(); toast('锁定好了！正式比赛会用这一版。'); }) }, `🔒 锁定第 ${st.latest} 版`)),
    h('p', { class: 'muted small' }, '正式比赛只用你锁定的版本。建议先“测试 30 局”，满意了再锁定。'),
  );
}

// ---------- practice & test ----------
function renderOpponents() {
  const sel = $('#opp') as HTMLSelectElement;
  $('#testChampBtn').classList.toggle('hidden', !st.champion);
  const ks = st.classmates.filter((c: any) => c.locked != null);
  if (unchanged(sel, [st.champion?.name, ks.map((c: any) => [c.id, c.name])])) return;
  const cur = sel.value;
  sel.innerHTML = '';
  if (st.champion) sel.append(h('optgroup', { label: '全班一起练出来的' }, h('option', { value: 'champion' }, st.champion.name)));
  sel.append(h('optgroup', { label: '机器人队' }, ...meta.bots.filter((b: any) => b.key !== 'bot:guard').map((b: any) => h('option', { value: b.key }, b.name))));
  if (ks.length) sel.append(h('optgroup', { label: '同学（锁定版）' }, ...ks.map((c: any) => h('option', { value: `kid:${c.id}` }, c.name))));
  if (cur && [...sel.options].some((o) => o.value === cur)) sel.value = cur;
}

function showMatch(target: Player, sumEl: HTMLElement, d: ReplayData, extra?: string) {
  const names = d.result.replay!.names ?? ['我', '对手'];
  sumEl.innerHTML = '';
  sumEl.append(h('div', { class: 'big' }, matchSummary(d.result, names)));
  if (extra) sumEl.append(h('div', {}, extra));
  // rule triggers for my rules cars
  const hits = d.result.cars[0];
  d.teams[0].cars.forEach((c: CarSpec, i: number) => {
    if (c.kind !== 'rules' || !hits[i]) return;
    if (!c.rules.length) { sumEl.append(h('div', { class: 'muted small' }, `${i + 1}号车：没有规则`)); return; }
    sumEl.append(h('div', { class: 'muted small' }, `${i + 1}号车每条规则被用上的次数：`),
      h('ol', { class: 'muted small ruleuse' }, ...c.rules.map((r, k) => h('li', {}, `${ruleText(r)}　`, h('b', {}, `${hits[i].ruleHits[k] ?? 0} 次`)))));
  });
  target.load(d);
}

$('#practiceBtn').onclick = () => practice(($('#opp') as HTMLSelectElement).value, $('#practiceBtn') as HTMLButtonElement);
const practice = (opponent: string, btn: HTMLButtonElement | null) => busy(btn, '比赛中…', async () => {
  const d = await api.call('kid.practice', { opponent, mode: 'match' });
  let extra: string | undefined;
  if (d.vsChampion) {
    st.vsChampion = d.vsChampion;
    extra = d.result.winner === 0 ? '🎉 你赢了冠军一局！再点“测试 30 局（只打冠军）”，看看是不是真的能赢它。' : '这局输给了冠军。看看回放：它是怎么赢的？哪里有空子？';
    renderChampion();
  }
  showMatch(player, $('#summary'), d, extra);
  if (btn !== $('#practiceBtn')) $('#summary').scrollIntoView({ behavior: 'smooth' });
});
$('#warmBtn').onclick = () => busy($('#warmBtn'), '比赛中…', async () => {
  const d = await api.call('kid.practice', { mode: 'warmup' });
  const t = d.result.firstCaptureTick[0];
  showMatch(player, $('#summary'), d, t >= 0 ? `🏁 运旗用时 ${(t / 10).toFixed(1)} 秒！再快一点试试？` : '这局 60 秒内没能把旗运回家。看看回放，卡在哪里了？');
});
$('#sandboxBtn').onclick = () => busy($('#sandboxBtn'), '比赛中…', async () => {
  const d = await api.call('kid.practice', { mode: 'sandbox' });
  showMatch(player, $('#summary'), d);
  const r = d.result;
  $('#summary').firstElementChild!.textContent = `空场练习（没有敌人）：2 分钟运回 ${r.captures[0]} 面旗，占能量点 ${Math.round(r.energySeconds[0])} 秒`;
});
$('#testBtn').onclick = () => test('mix', $('#testBtn') as HTMLButtonElement);
$('#testChampBtn').onclick = () => test('champion', $('#testChampBtn') as HTMLButtonElement);
const test = (against: 'mix' | 'champion', btn: HTMLButtonElement) => busy(btn, '跑 30 局…', async () => {
  const r = await api.call('kid.test', { against });
  const panel = $('#testPanel');
  panel.classList.remove('hidden');
  panel.innerHTML = '';
  const bar = (label: string, x: any) => h('div', { class: 'tbar' }, h('span', { class: 'tl' }, label),
    h('div', { class: 'track' }, h('div', { class: 'fill', style: `width:${Math.round(x.rate * 100)}%` })),
    h('span', {}, `赢 ${x.wins} 平 ${x.draws} 输 ${x.losses}（${Math.round(x.rate * 100)}%）`));
  panel.append(h('h2', {}, against === 'champion' ? '测试结果：只打冠军' : '测试结果'));
  if (against === 'champion') {
    panel.append(h('div', { class: `verdict ${r.beat ? 'good' : 'same'}` }, r.beat
      ? '🏆 你打败了冠军！你的打法已经放到“打败冠军的打法”里，大屏上也能看到。'
      : r.current.wins > 0 ? `赢了冠军 ${r.current.wins} 局，还没赢过它。再改改？` : '这 30 局一局也没赢。看看回放，它哪里有空子？'));
  } else if (r.verdict) panel.append(h('div', { class: `verdict ${r.verdict === '新版本更好' ? 'good' : r.verdict === '新版本更差' ? 'bad' : 'same'}` }, r.verdict));
  panel.append(bar(r.base ? `第${r.current.n}版（新）` : `第${r.current.n}版`, r.current));
  if (r.base) panel.append(bar(`第${r.base.n}版（旧）`, r.base));
  panel.append(h('p', { class: 'muted small' }, against === 'champion'
    ? `30 局都打冠军，两边各打 15 局。赢的局数比输的多，才算打败了它。`
    : `两个版本各打 ${r.games} 局，对手和随机种子完全一样（${r.opponents.join('、')}）。差距小于 ${r.noise}% 时，很可能只是运气，所以算“看不出差别”。`));
  r.current.hits.forEach((hs: number[], i: number) => {
    if (st.team.cars[i]?.kind !== 'rules') return;
    panel.append(h('div', { class: 'muted small' }, `新版 ${i + 1}号车 30 局里规则被用上：${hs.map((n, k) => `第${k + 1}条 ${n}次`).join('，') || '没有规则'}`));
  });
  panel.scrollIntoView({ behavior: 'smooth' });
  if (r.beat) refresh(true);
});

// ---------- the champion: the class trained it; everyone hunts for its weak spots ----------
function renderChampion() {
  const panel = $('#champPanel');
  const c = st.champion;
  panel.classList.toggle('hidden', !c);
  if (!c) return;
  panel.innerHTML = '';
  const bots = c.rates.slice(0, 4).map((r: number, j: number) => `${String(c.opponents[j]).replace('机器人·', '')} ${Math.round(r * 100)}%`).join('、');
  const vc = st.vsChampion;
  const cards = h('div', {}, ...usedRulesOnly(c.cars, c.used).map((car: any, i: number) => ruleCard(car.rules, car.params, { title: `${i + 1}号车` })));
  panel.append(
    h('h2', {}, '冠军 ', h('small', {}, '全班一起练出来的')),
    h('p', {}, c.rates.slice(0, 4).every((r: number) => r > 0.5)
      ? `没有人教它，它自己练到 4 个机器人全赢（${bots}）。它有弱点吗？`
      : `没有人教它，只告诉它输赢，它自己练出来的。打 4 个机器人的胜率：${bots}。它有弱点吗？`),
    h('div', { class: 'row wrap' },
      h('button', { class: 'btn primary', onclick: (e: any) => practice('champion', e.target) }, '练一局打冠军'),
      h('span', { class: 'muted small' }, vc.played ? `你打冠军：赢了 ${vc.won} 局，一共打了 ${vc.played} 局` : '')),
    h('details', {}, h('summary', {}, '看冠军的规则卡（只列出比赛里用上的规则）'), cards),
    h('h3', {}, '打败冠军的打法'),
  );
  if (!st.beats.length) panel.append(h('p', { class: 'muted small' }, '还没有人打败冠军。用“测试 30 局（只打冠军）”赢过它，你的打法就会出现在这里。'));
  for (const b of st.beats) {
    panel.append(h('div', { class: 'beat' },
      h('div', { class: 'row' }, h('b', {}, b.mine ? '我的打法' : `${b.name} 的打法`), h('span', { class: 'muted small' }, `打冠军 30 局：赢 ${b.wdl[0]} 平 ${b.wdl[1]} 输 ${b.wdl[2]}`),
        b.mine ? '' : h('button', { class: 'btn tiny', title: '把这支队的 3 辆车换成这个打法（会存成新版本，随时能回到旧版）', onclick: () => borrow(b) }, '借来改')),
      ...teamLines(b.team).map((l) => h('div', { class: 'small' }, l))));
  }
}

const borrow = (b: any) => busy(null, '', async () => {
  if (!confirm(`把你的队换成 ${b.name} 的打法？你现在的队会留在版本里，随时能回去。`)) return;
  st.team = await api.call('kid.borrow', { id: b.id });
  highlight = null;
  await refresh(true);
  toast(`借来了 ${b.name} 的打法。改一改，变成你自己的！`);
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

// ---------- rounds ----------
function renderRounds() {
  const box = $('#rounds');
  box.innerHTML = '';
  if (!st.rounds.length) { box.append(h('p', { class: 'muted' }, '还没有正式比赛。')); return; }
  for (const r of [...st.rounds].reverse()) {
    const sec = h('div', { class: 'round' }, h('h3', {}, `第 ${r.n} 轮${r.kind === 'final' ? '（决赛）' : r.kind === 'challenge' ? '（挑战 4 个机器人）' : ''}`));
    for (const row of r.rows) {
      const w = row.vs.filter((v: any) => v.wdl[0] > v.wdl[2]).length;
      sec.append(h('div', { class: 'big' }, `${row.name}：${row.vs.length} 个对手赢了 ${w} 个`));
      const cls = (v: any) => (v.wdl[0] > v.wdl[2] ? 'win' : v.wdl[0] < v.wdl[2] ? 'loss' : 'draw');
      // a fresh game between the same two teams (new seed), so it can come out either way
      const watch = (v: any) => busy(null, '', async () => {
        const d = await api.call('kid.roundReplay', { n: r.n, a: r.myKeys[r.rows.indexOf(row)], b: v.key });
        showMatch(roundPlayer, $('#roundSummary'), d);
      });
      const line = (label: string, k: number) => h('tr', {}, h('th', {}, label), ...row.vs.map((v: any) => h('td', { class: cls(v), onclick: () => watch(v) }, String(v.wdl[k]))));
      sec.append(h('table', { class: 'matrix kidvs' },
        h('tr', {}, h('th', {}, '对手'), ...row.vs.map((v: any) => h('th', { class: cls(v) }, v.name.replace('机器人·', '')))),
        line('赢', 0), line('平', 1), line('输', 2),
        h('tr', {}, h('th', {}, ''), ...row.vs.map((v: any) => h('td', { class: 'go' }, h('button', { class: 'btn tiny', onclick: () => watch(v) }, '看一局'))))));
    }
    box.append(sec);
  }
}
